import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Send, BookOpen, Ticket, Headphones } from "lucide-react";
import { PageFrame, SectionCard } from "@/components/common";
import { Button, ToggleGroup, buttonVariants } from "@/components/ui";
import { FAQSection, SupportTicketForm } from "./components";
import {
  SUPPORT_SUBJECT_PARAM,
  SUPPORT_TAB_PARAM,
  parseSupportTab,
  type SupportTab,
} from "./supportPaths";

const TAB_ICONS = { faq: BookOpen, ticket: Send } as const;

export default function SupportPage() {
  const { t } = useTranslation("support");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseSupportTab(searchParams.get(SUPPORT_TAB_PARAM));
  const initialSubject = searchParams.get(SUPPORT_SUBJECT_PARAM) ?? "";
  const setActiveTab = (tab: SupportTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "faq") next.delete(SUPPORT_TAB_PARAM);
    else next.set(SUPPORT_TAB_PARAM, tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <PageFrame
      title={t("pageTitle")}
      description={t("pageDescription")}
      actions={
        <Link
          to="/support/tickets"
          className={buttonVariants({ variant: "outline" })}
        >
          <Ticket className="h-4 w-4" />
          {t("myTickets")}
        </Link>
      }
    >
      <div className="-mx-1 overflow-x-auto px-1 pb-0.5">
        <ToggleGroup<SupportTab>
          variant="segmented-neutral"
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel={t("pageTitle")}
          options={(["faq", "ticket"] as const).map((id) => ({
            value: id,
            icon: TAB_ICONS[id],
            label: t(`tabs.${id}`),
          }))}
        />
      </div>

      <SectionCard
        className="animate-slide-up overflow-hidden"
        title={
          activeTab === "faq"
            ? t("sections.faqTitle")
            : t("sections.ticketTitle")
        }
        description={
          activeTab === "faq"
            ? t("sections.faqDescription")
            : t("sections.ticketDescription")
        }
        contentClassName="px-5 py-4"
      >
          {activeTab === "faq" ? (
            <div className="space-y-4">
              <FAQSection />
              <div className="flex flex-col items-start gap-4 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between dark:bg-surface-100/40">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-600/15">
                    <Headphones className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-surface-900">
                      {t("needHelp.title")}
                    </p>
                    <p className="mt-0.5 text-[13px] text-surface-500">
                      {t("needHelp.description")}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setActiveTab("ticket")}
                  leftIcon={<Send className="h-4 w-4" />}
                  className="shrink-0"
                >
                  {t("needHelp.action")}
                </Button>
              </div>
            </div>
          ) : (
            <SupportTicketForm initialSubject={initialSubject} />
          )}
        </SectionCard>
    </PageFrame>
  );
}
